import type { PerspectiveLensScore, PerspectiveReportDimension } from "~vue/appTypes";
import { i18n } from "~vue/i18n";
import { API_ARCHETYPE_LETTERS, type Dimension, PERSPECTIVE_LENS_CARD_DATA, PERSPECTIVE_TYPES, type PerspectiveLens, TYPE_COLORS } from "~vue/pages/perspectiveTypes";

const DIMENSION_CENTER_SCORE = 0;
const DIMENSION_MIN_SCORE = -3;
const DIMENSION_MAX_SCORE = 3;

/** Maps a binned score (−3 … +3) to a percentage (0 … 100) along the dimension track. */
export const scoreToPercent = (binnedScore: number): number => {
    const clamped = Math.min(Math.max(binnedScore, DIMENSION_MIN_SCORE), DIMENSION_MAX_SCORE);
    return ((clamped - DIMENSION_MIN_SCORE) / (DIMENSION_MAX_SCORE - DIMENSION_MIN_SCORE)) * 100;
};

export type DimensionSummary = {
    dimension: Dimension;
    summary: string;
    shortSummary: string;
    percent: number;
    leftLabel: string;
    rightLabel: string;
};

type LeanStrength = "slight" | "moderate" | "strong";

const leanStrength = (magnitude: number): LeanStrength => (magnitude >= 3 ? "strong" : magnitude === 2 ? "moderate" : "slight");

/** A score that is neither below nor above the center (including NaN) reads as balanced. */
const leanSide = (score: number): "left" | "right" | null => (score < DIMENSION_CENTER_SCORE ? "left" : score > DIMENSION_CENTER_SCORE ? "right" : null);

/**
 * The sentence for where a binned score sits on a dimension, e.g. "You tend to be more Extravert than Introvert."
 * Translated at call time, so call it from a computed or render function to follow a language switch.
 */
export const describeDimensionTendency = (binnedScore: number, leftLabel: string, rightLabel: string): string => {
    const side = leanSide(binnedScore);
    if (side === null) {
        return i18n.global.t("perspective.summary.tendencyBalanced", { left: leftLabel, right: rightLabel });
    }
    const leansLeft = side === "left";
    return i18n.global.t("perspective.summary.tendencyLeaning", {
        strength: leanStrength(Math.abs(binnedScore)),
        dominant: leansLeft ? leftLabel : rightLabel,
        other: leansLeft ? rightLabel : leftLabel,
    });
};

export const calculateDimensionSummary = (dimension: PerspectiveReportDimension): DimensionSummary => {
    const binnedScore = dimension.binned_score;
    const percent = scoreToPercent(binnedScore);
    const leftLabel = dimension.left_label;
    const rightLabel = dimension.right_label;
    const side = leanSide(binnedScore);
    const shortSummary =
        side === null
            ? i18n.global.t("perspective.summary.shortBalanced")
            : i18n.global.t("perspective.summary.shortLeaning", {
                  strength: Math.abs(binnedScore) >= 2 ? "primary" : "slight",
                  side: side === "left" ? leftLabel : rightLabel,
              });

    return {
        dimension: dimension.dimension,
        summary: describeDimensionTendency(binnedScore, leftLabel, rightLabel),
        shortSummary,
        percent,
        leftLabel,
        rightLabel,
    };
};

export const calculateLensSummary = (lensScore: PerspectiveLensScore, leftLabel: string, rightLabel: string): string => {
    const binnedScore = lensScore.binned_score;
    const clampedScore = Math.min(Math.max(binnedScore, DIMENSION_MIN_SCORE), DIMENSION_MAX_SCORE);

    const side = leanSide(clampedScore);
    if (side === null) {
        return i18n.global.t("perspective.summary.lensBalanced", { left: leftLabel, right: rightLabel });
    }
    return i18n.global.t("perspective.summary.lensLeaning", {
        strength: leanStrength(Math.abs(clampedScore)),
        side: side === "left" ? leftLabel : rightLabel,
    });
};

export const mapBinnedScoreToCardText = (lens: PerspectiveLens, binnedScore: number): string => {
    const cardData = PERSPECTIVE_LENS_CARD_DATA[lens];
    if (binnedScore <= -2) {
        return cardData.cardNegative;
    } else if (binnedScore === -1) {
        return cardData.cardSlightlyNegative;
    } else if (binnedScore === 0) {
        return cardData.cardNeutral;
    } else if (binnedScore === 1) {
        return cardData.cardSlightlyPositive;
    } else {
        return cardData.cardPositive;
    }
};

export const getPerspectiveColor = (letters: string | null | undefined): string | null => {
    if (!letters) {
        return null;
    }
    return TYPE_COLORS[letters] ?? null;
};

export const getPerspectiveLetters = (archetype: string | null | undefined): string | undefined => (archetype ? API_ARCHETYPE_LETTERS[archetype] : undefined);

export const getPerspectiveProfileTitle = (letters: string | null | undefined): string | null => {
    if (!letters) {
        return null;
    }
    const title = PERSPECTIVE_TYPES[letters]?.title;
    return title ? i18n.global.t("perspective.profileTitle", { title }) : null;
};
